import { useState, useEffect, useRef, Suspense, useCallback } from "react";
import { useRouter } from "next/router";
import { useRecoilState } from "recoil";
import parseISO from "date-fns/parseISO";
import differenceInSeconds from "date-fns/differenceInSeconds";
import MuxPlayer from "@mux/mux-player-react";
import clsx from "clsx";

import {
  getGroupForTitleEngage,
  getItemNumber,
  getGroupForTitle,
} from "../../helpers/text";
import {
  getSubheadingByGroupId,
  getNameByGroupId,
  getGroupSubheadingInfo,
  getParentByGroupId,
} from "../../helpers/arrays";
import { engageState } from "../../atoms/platform";
import { apiGetVideoInfo } from "../../lib/api/engage";

import { Tab } from "@headlessui/react";
import { FaRegPlayCircle } from "react-icons/fa";

import Loading from "../Shared/Loading";
import InvalidMeeting from "../Engage/InvalidMeeting";
import AdobeViewer from "../Pdf/AdobeViewer";
import VideoShareButtons from "../Engage/VideoShareButtons";
import AgendaTopicPlayerToc from "../Engage/AgendaTopicPlayerToc";
import VttChapters from "../Engage/VTTChapters";
import UserWayWidget from "../UserWayWidget";

// TODO Figure out on mobile how to show pdf

function Player() {
  const router = useRouter();
  const { client, id } = router.query;
  const inputRange = useRef(null);
  const [platform, setPlatform] = useRecoilState(engageState);
  const [agenda, setAgenda] = useState();
  const [active, setActive] = useState();
  const [mobileDetails, setMobileDetails] = useState(false);
  const [activeVideo, setActiveVideo] = useState();
  const [skipCompare, setSkipCompare] = useState([]);
  const [loading, setLoading] = useState(false);
  const [invalidMeeting, setInvalidMeeting] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [topicDetails, setTopicDetails] = useState();
  const [currentTime, setCurrentTime] = useState(0);

  useEffect(() => {
    async function fetchVideo() {
      setLoading(true);
      const result = await apiGetVideoInfo(id);
      if (result) {
        const { meeting } = result;
        setPlatform(result.options);
        let dateRight;
        if (meeting.videoStartTime) {
          dateRight = parseISO(meeting.videoStartTime);
        } else {
          if (meeting.startIso) {
            dateRight = parseISO(meeting.startIso);
          }
        }
        setAgenda(meeting);
        let skips = [];
        for (const g of meeting.groupOrder) {
          const groupActions = meeting.topics
            .map((topic) => {
              // console.log(topic);
              if (topic.agendaGroup.toString() === g._id.toString()) {
                if (!topic.bulkTopic) {
                  return topic;
                } else {
                  return null;
                }
              }
              return null;
            })
            .filter((topic) => topic !== null);
          groupActions.sort((a, b) => a.order - b.order);
          for (const i of groupActions) {
            if (i.startTime) {
              if (dateRight) {
                const dateLeft = parseISO(i.startTime);
                const result = differenceInSeconds(dateLeft, dateRight, {
                  roundingMethod: "ceil",
                });
                skips.push({ seconds: result, ...i });
              }
            }
          }
        }
        setSkipCompare(skips);
        const compare = window.innerWidth;
        if (compare > 767) {
          setActiveVideo(1);
        } else {
          setActiveVideo(2);
        }
        setLoading(false);
      } else {
        setLoading(false);
        setInvalidMeeting(true);
      }
    }
    if (id) {
      fetchVideo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleProgress = useCallback(
    (e) => {
      const number = Math.ceil(e.target.currentTime);
      setCurrentTime(e.target.currentTime); // add this line
      const index = skipCompare.findIndex((e) => e.seconds === number);
      if (index >= 0 && active?._id !== skipCompare[index]._id) {
        setActive(skipCompare[index]);
      }
    },
    [skipCompare, active],
  );

  function handleTopicChosen(topic) {
    setActive();
    if (topic.elapsedSec) {
      inputRange.current.currentTime = topic.elapsedSec;
    } else {
      const match = skipCompare.find((e) => e._id === topic._id);
      inputRange.current.currentTime = match?.seconds ?? 0;
    }

    setTimeout(() => {
      if (topic.attachments.length > 0) {
        setTopicDetails(topic);
        setShowDetails(true);
      }
    }, 750);
  }

  function handleGroupChosen(elapsedSec) {
    // setActive();
    inputRange.current.currentTime = elapsedSec;
  }

  // const handleProgress = (state) => {
  //   const number = Math.ceil(state.playedSeconds);
  //   const index = skipCompare.findIndex((e) => e.seconds === number);
  //   // auto jump to the topic based on the ceiling of played seconds
  //   if (index >= 0) {
  //     setActive();
  //     setTimeout(() => {
  //       setActive(skipCompare[index]);
  //     }, 1000);
  //   }
  // };

  return (
    <div className="h-screen overflow-hidden bg-white">
      {/* Desktop Top Banner */}
      <div className="hidden bg-slate-900 py-2 xl:py-0 xl:h-16 w-full sm:flex justify-start items-center space-x-2 xl:space-x-6 px-2 2xl:px-12">
        <p className="text-xs md:text-sm lg:text-base xl:text-lg font-medium text-white mr-5">
          {platform?.clientName}
        </p>
        <h3 className="text-mini md:text-xs lg:text-sm xl:text-lg font-medium text-center text-white">
          {agenda?.meetingType}
        </h3>
        <p className="text-mini md:text-xs lg:text-sm xl:text-lg italic text-center text-zinc-200">
          {agenda?.scheduleString}
        </p>
      </div>
      {/* Desktop Content */}
      <div className="hidden sm:grid sm:grid-cols-4 2xl:grid-cols-12 h-full overflow-hidden bg-white">
        {/* Main Area */}
        <div
          className={clsx("sm:col-span-2 2xl:col-span-6 overflow-y-auto")}
          // className={clsx(
          //   agenda?.archiveVideo
          //     ? "sm:col-span-2 2xl:col-span-6 overflow-y-auto"
          //     : "sm:col-span-3 2xl:col-span-8 overflow-y-auto"
          // )}
        >
          {/* Video Player */}
          <div className="sticky top-0 z-50 bg-black">
            <div className="relative aspect-w-16 aspect-h-9 w-full">
              <Suspense fallback={<div className="w-full h-full bg-black" />}>
                {activeVideo && activeVideo === 1 && (
                  <MuxPlayer
                    ref={inputRange}
                    src={agenda?.videoUrl}
                    streamType="on-demand"
                    onTimeUpdate={handleProgress}
                    style={{ width: "100%", height: "100%" }}
                  />
                )}
              </Suspense>
            </div>
          </div>
          <div className="bg-slate-900 h-full w-full">
            <Tab.Group>
              <Tab.List className="flex px-2 2xl:px-6">
                <Tab
                  className={({ selected }) =>
                    clsx(
                      "w-full py-1.5 2xl:py-2.5 text-mini xl:text-sm 2xl:text-base leading-5 font-medium text-blueGray-200 rounded-t-md",
                      "focus:outline-none",
                      selected
                        ? "bg-coolGray-700"
                        : "hover:bg-white/[0.12] hover:text-white border border-coolGray-700",
                    )
                  }
                >
                  Chapters
                </Tab>
                <Tab
                  className={({ selected }) =>
                    clsx(
                      "w-full py-1.5 2xl:py-2.5 text-mini xl:text-sm 2xl:text-base leading-5 font-medium text-blueGray-200 rounded-t-md",
                      "focus:outline-none",
                      selected
                        ? "bg-coolGray-700"
                        : "hover:bg-white/[0.12] hover:text-white border border-coolGray-700",
                    )
                  }
                >
                  Share
                </Tab>
                {platform?.playerTranscript && (
                  <Tab
                    className={({ selected }) =>
                      clsx(
                        "w-full py-1.5 2xl:py-2.5 text-mini xl:text-sm 2xl:text-base leading-5 font-medium text-blueGray-200 rounded-t-md",
                        "focus:outline-none",
                        selected
                          ? "bg-coolGray-700"
                          : "hover:bg-white/[0.12] hover:text-white border border-coolGray-700",
                      )
                    }
                  >
                    Transcript
                  </Tab>
                )}
              </Tab.List>
              <Tab.Panels>
                <Tab.Panel>
                  <div className="overflow-y-auto bg-slate-900 pb-sm">
                    <div className="relative mx-2 2xl:mx-6">
                      {/* Content */}
                      <div className="flex flex-col">
                        {!agenda?.archiveVideo ? (
                          <>
                            <ul className="space-y-1 overflow-y-auto">
                              {agenda?.groupOrder.map((group, gIdx) => {
                                // todo figure out if the group has elapsed seconds
                                const name = getNameByGroupId(
                                  group._id,
                                  agenda?.groupSubheadings,
                                  group.name,
                                );
                                const groupInfo = getGroupSubheadingInfo(
                                  group._id,
                                  agenda?.groupSubheadings,
                                );
                                const groupActions = agenda?.topics
                                  .map((topic) => {
                                    if (
                                      topic.agendaGroup._id.toString() ===
                                      group._id.toString()
                                    ) {
                                      if (!topic.bulkTopic) {
                                        return topic;
                                      } else {
                                        return null;
                                      }
                                    }
                                    return null;
                                  })
                                  .filter((topic) => topic !== null);
                                groupActions.sort((a, b) => a.order - b.order);
                                const allZeroOrNull = groupActions.every(
                                  (topic) => !topic.elapsedSec,
                                );
                                return (
                                  <div key={`group-${gIdx}`}>
                                    {groupActions.length > 0 ? (
                                      <div
                                        className={clsx(
                                          groupInfo?.elapsedSec &&
                                            "hover:bg-zinc-600 transform duration-200 cursor-pointer",
                                          allZeroOrNull &&
                                            client === "farmersbranchtx" &&
                                            groupInfo.groupId !==
                                              "67521a0f1771a90054fac31c" && // Consent Agenda
                                            "hidden",
                                          "mt-2 pl-1.5 py-0.5 rounded-md",
                                        )}
                                        onClick={() => {
                                          if (groupInfo?.elapsedSec) {
                                            handleGroupChosen(
                                              groupInfo?.elapsedSec,
                                            );
                                          }
                                        }}
                                      >
                                        <p
                                          className={clsx(
                                            groupInfo?.elapsedSec
                                              ? "text-zinc-200"
                                              : "text-zinc-400",
                                            "text-sm 2xl:text-base inline-flex items-center select-none",
                                          )}
                                        >
                                          {groupInfo?.elapsedSec !== 0 && (
                                            <FaRegPlayCircle className="h-3 w-3 mr-2 text-zinc-200" />
                                          )}
                                          {getGroupForTitleEngage(
                                            agenda?.groupNumbering,
                                            gIdx,
                                            agenda?.groupOrder,
                                            agenda?.childNumbering,
                                          )}
                                          {name}
                                        </p>
                                      </div>
                                    ) : (
                                      <div
                                        className={clsx(
                                          groupInfo?.elapsedSec &&
                                            "hover:bg-zinc-600 transform duration-200 cursor-pointer",
                                          !groupInfo?.elapsedSec &&
                                            client === "farmersbranchtx" &&
                                            "hidden",
                                          "mt-2 pl-1.5 py-0.5 rounded-md",
                                        )}
                                        onClick={() => {
                                          if (groupInfo?.elapsedSec) {
                                            handleGroupChosen(
                                              groupInfo?.elapsedSec,
                                            );
                                          }
                                        }}
                                      >
                                        <p
                                          className={clsx(
                                            groupInfo?.elapsedSec
                                              ? "text-zinc-200"
                                              : "text-zinc-400",
                                            "text-sm 2xl:text-base inline-flex items-center select-none",
                                          )}
                                        >
                                          {groupInfo?.elapsedSec && (
                                            <FaRegPlayCircle className="h-3 w-3 mr-2 text-zinc-200" />
                                          )}
                                          {getGroupForTitleEngage(
                                            agenda?.groupNumbering,
                                            gIdx,
                                            agenda?.groupOrder,
                                            agenda?.childNumbering,
                                          )}
                                          {name}
                                        </p>
                                      </div>
                                    )}
                                    {groupActions.map((topic, tIdx) => {
                                      if (
                                        client === "breaca" ||
                                        client === "farmersbranchtx"
                                      ) {
                                        if (topic.elapsedSec !== 0) {
                                          return (
                                            <li
                                              key={topic._id}
                                              onClick={() =>
                                                handleTopicChosen(topic)
                                              }
                                            >
                                              <div className="py-2 2xl:py-3 hover:bg-zinc-600 transform duration-200 rounded-md">
                                                <p
                                                  className={clsx(
                                                    active?._id === topic._id
                                                      ? "text-white"
                                                      : "text-zinc-200",
                                                    "ml-2 lg:ml-4 text-sm 2xl:text-base font-medium cursor-pointer transition duration-200 2xl:text-justify inline-flex items-center",
                                                  )}
                                                >
                                                  <FaRegPlayCircle className="h-3 w-3 mr-2 text-zinc-200 flex-shrink-0" />
                                                  {getGroupForTitle(
                                                    agenda?.groupNumbering,
                                                    gIdx,
                                                    agenda?.groupOrder,
                                                    agenda?.childNumbering,
                                                  )}
                                                  {getItemNumber(
                                                    agenda?.topics,
                                                    agenda?.itemNumbering,
                                                    topic,
                                                    tIdx,
                                                    agenda?.groupNumbering,
                                                    agenda?.childNumbering,
                                                  )}
                                                </p>
                                              </div>
                                            </li>
                                          );
                                        } else {
                                          return null;
                                        }
                                      } else {
                                        return (
                                          <li
                                            key={topic._id}
                                            onClick={() =>
                                              handleTopicChosen(topic)
                                            }
                                          >
                                            <div className="py-2 2xl:py-3 hover:bg-zinc-600 transform duration-200 rounded-md">
                                              <p
                                                className={clsx(
                                                  active?._id === topic._id
                                                    ? "text-white"
                                                    : "text-zinc-200",
                                                  "ml-2 lg:ml-4 text-sm 2xl:text-base font-medium cursor-pointer transition duration-200 2xl:text-justify inline-flex items-center",
                                                )}
                                              >
                                                <FaRegPlayCircle className="h-3 w-3 mr-2 text-zinc-200 flex-shrink-0" />
                                                {getGroupForTitle(
                                                  agenda?.groupNumbering,
                                                  gIdx,
                                                  agenda?.groupOrder,
                                                  agenda?.childNumbering,
                                                )}
                                                {getItemNumber(
                                                  agenda?.topics,
                                                  agenda?.itemNumbering,
                                                  topic,
                                                  tIdx,
                                                  agenda?.groupNumbering,
                                                  agenda?.childNumbering,
                                                )}
                                              </p>
                                            </div>
                                          </li>
                                        );
                                      }
                                    })}
                                  </div>
                                );
                              })}
                            </ul>
                            {loading && <Loading />}
                          </>
                        ) : (
                          <p className="text-white 2xl:text-xl font-medium text-center mt-8">
                            No Chapters Available
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </Tab.Panel>
                <Tab.Panel>
                  <div className="min-h-96 overflow-y-auto mx-2 2xl:mx-6">
                    <div className="relative">
                      {/* Content */}

                      <VideoShareButtons />
                    </div>
                  </div>
                </Tab.Panel>
                {platform?.playerTranscript && (
                  <Tab.Panel>
                    <div className="min-h-96 overflow-y-auto mx-2 2xl:mx-6">
                      {/* Content */}
                      <VttChapters
                        videoUrl={agenda?.videoUrl}
                        onJump={(time) => {
                          inputRange.current.currentTime = time;
                        }}
                        captionsTrack={agenda?.captionTrackId}
                        currentTime={currentTime}
                      />
                    </div>
                  </Tab.Panel>
                )}
              </Tab.Panels>
            </Tab.Group>
          </div>
        </div>
        {/* SideBar */}
        <div className="hidden sm:block sm:col-span-2 2xl:col-span-6 px-2 border-l border-slate-600 overflow-y-auto h-full">
          {showDetails && (
            <AgendaTopicPlayerToc
              visible={setShowDetails}
              topic={topicDetails?.id}
              client={client}
            />
          )}
          <AdobeViewer
            url={agenda?.agendaPdfUrl}
            fileName={`${agenda?.department?.name}.pdf`}
          />
        </div>
      </div>

      {/* Mobile */}
      <div className="sm:hidden flex flex-col h-full overflow-y-auto">
        {/* Video Player */}
        <div className="sticky top-0 left-0 w-full h-[14rem] z-20">
          <Suspense fallback={<div className="w-full h-full bg-black" />}>
            {activeVideo && activeVideo === 2 && (
              <MuxPlayer
                ref={inputRange}
                src={agenda?.videoUrl}
                streamType="on-demand"
                onTimeUpdate={handleProgress}
                style={{ width: "100%", height: "14rem" }}
              />
            )}
          </Suspense>
        </div>
        {/* Mobile Heading */}
        <p className="text-sm md:text-base text-center font-medium text-black px-4 mt-1">
          {platform?.clientName}
        </p>
        <p className="text-xs md:text-sm text-center font-medium text-black px-4">
          {agenda?.department?.name}
        </p>
        <p className="text-xs md:text-sm text-center italic text-black px-4">
          {agenda?.scheduleString}
        </p>
        {/* Agenda  */}
        <div className="mt-4">
          {agenda?.groupOrder.map((group, index) => {
            const subheading = getSubheadingByGroupId(
              group._id,
              agenda?.groupSubheadings,
            );
            const parentGroup = getParentByGroupId(
              group.id,
              agenda?.groupSubheadings,
            );
            const name = getNameByGroupId(
              group._id,
              agenda?.groupSubheadings,
              group.name,
            );
            const groupInfo = getGroupSubheadingInfo(
              group._id,
              agenda?.groupSubheadings,
            );
            const groupActions = agenda?.topics
              .map((topic) => {
                if (topic.agendaGroup._id.toString() === group._id.toString()) {
                  if (!topic.bulkTopic) {
                    return topic;
                  } else {
                    return null;
                  }
                }
                return null;
              })
              .filter((topic) => topic !== null);
            groupActions.sort((a, b) => a.order - b.order);
            return (
              <div
                key={index}
                className={clsx(group?.parentGroup && "ml-2", "mb-4")}
              >
                <div
                  className="border-b border-gray-800"
                  onClick={() => {
                    if (groupInfo?.elapsedSec) {
                      handleGroupChosen(groupInfo?.elapsedSec);
                    }
                  }}
                >
                  <p
                    className={clsx(
                      groupInfo?.elapsedSec
                        ? "text-blue-700 hover:text-blue-400 cursor-pointer"
                        : "text-black",
                      "px-2 text-sm font-semibold",
                    )}
                  >
                    {getGroupForTitleEngage(
                      agenda?.groupNumbering,
                      index,
                      agenda?.groupOrder,
                      agenda?.childNumbering,
                    )}{" "}
                    {name}
                  </p>
                </div>
                {groupActions.map((topic, i) => (
                  <div key={topic._id}>
                    <div
                      className={clsx(
                        i === 0 ? "mt-1" : "",
                        active?._id === topic._id
                          ? "text-amber-600 font-bold scale-105"
                          : "text-blue-700 hover:text-blue-400",
                        "ml-4 mb-1 transform duration-200",
                      )}
                      onClick={() => {
                        handleTopicChosen(topic);
                        setMobileDetails(true);
                      }}
                    >
                      <p className="inline-flex text-xs font-medium">
                        {getGroupForTitle(
                          agenda?.groupNumbering,
                          index,
                          agenda?.groupOrder,
                          agenda?.childNumbering,
                        )}
                        {getItemNumber(
                          agenda?.topics,
                          agenda?.itemNumbering,
                          topic,
                          i,
                          agenda?.groupNumbering,
                          agenda?.childNumbering,
                        )}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>

        {loading && <Loading />}
      </div>
      {invalidMeeting && <InvalidMeeting />}
      <UserWayWidget />
    </div>
  );
}

export default Player;
